/**
 * Types and constants for Admin Settings
 */
import { MenuVisibility, SiteRegionalSettings, PreferencesChoices, DashboardTabsVisibility, CustomerCardFieldsVisibility, DataImportTilesVisibility } from '../../../lib/api';

// Portal types
export const PORTAL_TYPES = [
  { value: '1', label: 'Supplier Portal' },
  { value: '2', label: 'Customer Portal' },
];

export type NotificationEmailMode = 'off' | 'digest' | 'instant';

export interface NotificationMilestoneConfig {
  in_app: boolean;
  email: NotificationEmailMode;
}

/** Order-progress notification matrix + digest cadence, stored as JSON on
 *  PlatformSettings.default_notification_preferences. Extra keys (e.g. the
 *  digest watermark, owned by the backend) are tolerated and preserved. */
export type NotificationCadence = 'off' | 'hourly' | 'daily';

export interface CustomerCadenceOverride {
  name: string;
  cadence: NotificationCadence;
  hour: number;
}

export interface NotificationConfig {
  milestones?: Record<string, NotificationMilestoneConfig>;
  digest_cadence?: NotificationCadence;
  digest_hour?: number;
  /** Per-customer cadence overrides, keyed by Company id. */
  customer_overrides?: Record<string, CustomerCadenceOverride>;
  [key: string]: unknown;
}

export interface ThemeSettings {
  companyName: string;
  loginTitle: string;
  defaultTheme: number;
  availableThemes: string[];
  backgroundColor: string;
  loginColor: string;
  fontColor: string;
  buttonColor: string;
  website: string;
  supportEmail: string;
  salesEmail: string;
  noReplyEmail: string;
  contactEmail: string;
  companyAddress: string;
  companyPhone: string;
  emailSubject: string;
  emailSignature: string;
}

export interface PlatformSettings {
  mainMenuType: string;
  freeMode: boolean;
  mainCompanyCanLoginAs: boolean;
  automationRelated: boolean;
  useDispositionInNCR: boolean;
  warehouseModule: boolean;
  turnEmailMAB: boolean;
  switchPartRepository: boolean;
  qualityMeasurePPK: boolean;
  restrictCountry: string;
  restrictCountryEnabled: boolean;
  menuVisibility: MenuVisibility;
  ncrTemplate: 1 | 2;
  // Upload Limits (Feature 5)
  maxUploadSizeMb: number;
  allowedFileTypes: string[];
  // Document Versioning (Feature 8)
  autoVersionDocuments: boolean;
  versionFormat: 'numeric' | 'semantic';
  retainOldVersions: boolean;
  // Order-progress notification matrix (platform master gate)
  defaultNotificationPreferences: NotificationConfig;
  systemAlertEmails: string;
  /** Global kill switch — when true the platform sends no outbound emails. */
  emailsPaused: boolean;
  /** Per-recipient cap on emails in any rolling 24h. Overridable per site and
   *  per customer company; transactional/security mail is always exempt. */
  emailRateLimitPerDay: number;
  /** When true, notification emails go only to users who have joined. */
  notifyJoinedUsersOnly: boolean;
  // Hierarchy Labels
  hierarchyLevel1Label: string;
  hierarchyLevel2Label: string;
  hierarchyLevel3Label: string;
  // Default landing page for approved customer users
  customerDefaultLandingPath: string;
  // Feature-flagged modules
  poLineSchedules: boolean;
  jobDocuments: boolean;
}

export interface BarrattFlags {
  delivery_appeals: boolean;
  spend_leakage: boolean;
  bulk_upload: boolean;
  commodity_codes: boolean;
  allow_customer_decline: boolean;
  show_forecast_total: boolean;
  portal_mode: 'standard' | 'compliance' | 'supplier_portal';
}

export const DEFAULT_THEME_SETTINGS: ThemeSettings = {
  companyName: 'Helix',
  loginTitle: '',
  defaultTheme: 1,
  availableThemes: ['theme1', 'theme2'],
  backgroundColor: '#ffffff',
  loginColor: '#0066cc',
  fontColor: '#333333',
  buttonColor: '#0066cc',
  website: 'https://helix.com',
  supportEmail: 'support@helix.com',
  salesEmail: 'sales@helix.com',
  noReplyEmail: 'noreply@helix.com',
  contactEmail: '',
  companyAddress: '',
  companyPhone: '',
  emailSubject: 'Helix Reset Password',
  emailSignature: 'Helix Team',
};

export const DEFAULT_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': false,
  'scheduled': false,
  'spot-orders': false,
  'sales-orders': true,
  'forecast-forward': true,
  'cached-performance': false,
  'group-kpi': false,
  'group-detail-kpi': false,
  'site-kpi': false,
  'cost': false,
  'emissions': false,
  'compliance': false,
  'future-forecast': false,
  'date-adherence': false,
  'expediting': false,
  'performance-ratings': false,
};

export const DEFAULT_CUSTOMER_DASHBOARD_TABS: DashboardTabsVisibility = {
  'default': false,
  'scheduled': false,
  'spot-orders': false,
  'sales-orders': true,
  'forecast-forward': false,
  'cached-performance': false,
  'group-kpi': false,
  'group-detail-kpi': false,
  'site-kpi': false,
  'cost': true,
  'emissions': false,
  'compliance': true,
  'future-forecast': false,
  'date-adherence': true,
  'expediting': false,
  'performance-ratings': true,
};

/** Tabs default OFF until admin opts in (supplier Forecast Forward defaults ON). */
export const NEW_DASHBOARD_TABS_DEFAULT_OFF = new Set([
  'scheduled',
  'spot-orders',
  'forecast-forward',
  'cached-performance',
  'default',
  'group-kpi',
  'group-detail-kpi',
  'site-kpi',
  'cost',
  'emissions',
  'compliance',
  'future-forecast',
  'date-adherence',
  'expediting',
  'performance-ratings',
]);

export const DEFAULT_CUSTOMER_CARD_FIELDS: CustomerCardFieldsVisibility = {
  logo: true,
  risk_badge: true,
  otd: true,
  rating: true,
  questionnaire_status: true,
};

export const DEFAULT_DATA_IMPORT_TILES: DataImportTilesVisibility = {
  commodity: true,
  faultcodes: true,
  departments: true,
  warehouses: true,
  customers: true,
  customer_additional: true,
  grns: true,
  pos: true,
  ncrs: true,
  forecasts: true,
  users: true,
};

export const DEFAULT_BARRATT_FLAGS: BarrattFlags = {
  delivery_appeals: false,
  spend_leakage: false,
  bulk_upload: false,
  commodity_codes: false,
  allow_customer_decline: false,
  show_forecast_total: false,
  portal_mode: 'standard',
};

// Re-export for convenience
export type { MenuVisibility, SiteRegionalSettings, PreferencesChoices, DashboardTabsVisibility, CustomerCardFieldsVisibility, DataImportTilesVisibility };
